/**
 * Spec 031 A1: the PCs that run agents for the tasker. «Мои компьютеры» connects a PC by the code
 * it shows and disconnects it; the team's PCs are there to see, not to control. `CommandFacts`
 * draws a command's facts as the server keeps them — each one only when it happened.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Monitor, Plus } from "lucide-react";

import type { DeviceAbility } from "@/api/accounts";
import { api, ApiError } from "@/api/client";
import { Devices } from "@/api/resources";
import type { DeviceCommandOut, DeviceOut, DevicePairLookup } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { useToast } from "@/components/ui/Toast";
import { ago, Caption, Pill } from "@/kit";
import { abilityLine } from "@/lib/accounts";
import { commandActions, commandLive, deviceLine, launchesOnPcs, pairCode, stopsOf } from "@/lib/devices";

/** Spec 049/050: the PC's own emulator and speech to text, as its «Агенты» app reports them. */
type WithAbilities = DeviceOut & { android?: DeviceAbility | null; whisper?: DeviceAbility | null };

/** An agent's commands on my PCs: looked at every 3 seconds while one is on its way or its
 * process may live. Under «agents», so whatever refreshes the agent refreshes them too. */
export function useAgentCommands(agentId: string) {
  return useQuery({
    queryKey: ["agents", "device-commands", agentId],
    queryFn: () => Devices.agentCommands(agentId),
    refetchInterval: (q) => ((q.state.data ?? []).some(commandLive) ? 3000 : 30_000),
  });
}

/** On the agent's card: the launch that holds it on my PC and its stops — their facts and what
 * may be done, whether «Запустить» is open or not, after a reload too (review 7f478c78, 7). */
export function AgentPcCommands({ agentId, onTakeover }: { agentId: string; onTakeover: () => void }) {
  const qc = useQueryClient();
  const rows = useAgentCommands(agentId).data ?? [];
  const shown = launchesOnPcs(rows);
  if (!shown.length) return null;
  const changed = () => void qc.invalidateQueries({ queryKey: ["agents"] });
  return (
    <section className="space-y-3 rounded-card border border-p-border bg-p-surface px-4 py-3" aria-label="Команды на ПК">
      {shown.map((c) => (
        <div key={c.id} className="space-y-2" data-launch={c.id}>
          <p className="text-body font-semibold text-p-text">{c.subagent ? `Помощник «${c.subagent}» · ` : ""}запуск на ПК «{c.device_name}»</p>
          <CommandFacts command={c} onChanged={changed} />
          {stopsOf(rows, c.id).map((s) => <CommandFacts key={s.id} command={s} onChanged={changed} />)}
          {c.state === "unknown" && (
            <button type="button" className="min-h-11 text-meta font-semibold text-p-link" onClick={() => onTakeover()}>
              Перехватить…
            </button>
          )}
        </div>
      ))}
    </section>
  );
}

export function DevicesBlock() {
  const devices = useQuery({ queryKey: ["devices"], queryFn: Devices.list, refetchInterval: 30_000 });
  const [pairing, setPairing] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [revoking, setRevoking] = useState<DeviceOut | null>(null);
  const rows = devices.data ?? [];
  const mine = rows.filter((d) => d.mine);
  const team = rows.filter((d) => !d.mine);
  return (
    <section className="flex flex-col gap-2" aria-label="Компьютеры">
      <div className="flex items-center justify-between gap-2">
        <Caption>Мои компьютеры</Caption>
        <div className="flex flex-wrap justify-end gap-2">
          <Button size="compact" variant="ghost" icon={<Download className="h-4 w-4" aria-hidden="true" />} onClick={() => setInstalling(true)}>
            Установить приложение
          </Button>
          <Button size="compact" variant="secondary" icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setPairing(true)}>
            Подключить ПК
          </Button>
        </div>
      </div>
      {devices.isError && <p className="text-meta text-p-blocked">Не удалось загрузить компьютеры.</p>}
      {devices.data && mine.length === 0 && (
        <p className="text-meta text-p-text-muted">
          Подключи свой ПК: поставь на него приложение «Агенты» одной командой («Установить приложение»), в нём нажми «Подключить этот ПК» — появится код. Тогда агентов можно запускать отсюда.
        </p>
      )}
      {mine.length > 0 && (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
          {mine.map((d) => <DeviceRow key={d.id} d={d} onRevoke={() => setRevoking(d)} />)}
        </ul>
      )}
      {team.length > 0 && (
        <p className="text-meta text-p-text-muted">
          ПК команды: {team.map((d) => `${d.name} (${d.owner.name}, ${deviceLine(d).text})`).join(", ")}
        </p>
      )}
      {pairing && <PairSheet onClose={() => setPairing(false)} onInstall={() => { setPairing(false); setInstalling(true); }} />}
      {installing && <InstallSheet onClose={() => setInstalling(false)} />}
      {revoking && <RevokeSheet device={revoking} onClose={() => setRevoking(null)} />}
    </section>
  );
}

function DeviceRow({ d, onRevoke }: { d: DeviceOut; onRevoke: () => void }) {
  const line = deviceLine(d);
  const running = (d.terminals ?? []).filter((t) => t.alive).length;
  return (
    <li className="flex items-start gap-3 border-b border-p-border px-4 py-3 last:border-0">
      <Monitor className="mt-0.5 h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[15px] font-semibold leading-5 text-p-text">{d.name}</span>
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Pill tone={line.tone}>{line.text}</Pill>
          {!d.revoked_at && (
            <span className="text-meta text-p-text-muted">
              {[d.executor_version ? `исполнитель ${d.executor_version}` : null, d.cli?.claude ? `Claude Code ${d.cli.claude}` : null, running ? `агентов в работе: ${running}` : null,
                abilityLine("Android", (d as WithAbilities).android), abilityLine("Whisper", (d as WithAbilities).whisper)]
                .filter(Boolean)
                .join(" · ")}
            </span>
          )}
          {d.revoked_at && <span className="text-meta text-p-text-muted">отключён {ago(d.revoked_at)}</span>}
        </span>
      </span>
      {!d.revoked_at && (
        <Button size="compact" variant="ghost" onClick={onRevoke}>Отключить</Button>
      )}
    </li>
  );
}

/** The code from the PC's window: looked up first, so the person sees which PC they connect. */
function PairSheet({ onClose, onInstall }: { onClose: () => void; onInstall: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [typed, setTyped] = useState("");
  const [found, setFound] = useState<DevicePairLookup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const code = pairCode(typed);
  const lookup = useMutation({
    mutationFn: (c: string) => Devices.lookup(c),
    onSuccess: (r) => { setFound(r); setError(null); },
    onError: (e: Error) => setError(e.message),
  });
  const confirm = useMutation({
    mutationFn: (pairId: string) => Devices.confirm(pairId),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ["devices"] });
      toast.success(`ПК «${d.name}» подключён`);
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });
  return (
    <Sheet open onClose={onClose} title="Подключить ПК"
      footer={found
        ? <Button block variant="strong" loading={confirm.isPending} onClick={() => confirm.mutate(found.pair_id)}>Подключить «{found.name}»</Button>
        : <Button block variant="strong" disabled={!code} loading={lookup.isPending} onClick={() => code && lookup.mutate(code)}>Проверить код</Button>}>
      <div className="flex flex-col gap-3">
        <p className="text-body text-p-text-muted">
          На ПК открой приложение «Агенты» и нажми «Подключить этот ПК». Введи код из 8 цифр, который оно покажет.
        </p>
        <TextField label="Код с экрана ПК" inputMode="numeric" autoComplete="off" value={typed} placeholder="1234-5678"
          onChange={(e) => { setTyped(e.target.value); setFound(null); setError(null); }} />
        {found && (
          <p className="text-body text-p-text" data-pair-found>
            Это «{found.name}» · {found.platform === "windows" ? "Windows" : found.platform} · исполнитель {found.executor_version}. Подключаем его к тебе?
          </p>
        )}
        {error && <p className="text-meta text-p-blocked" role="alert">{error}</p>}
        <p className="text-meta text-p-text-muted">
          ПК делает свой ключ сам, сервер хранит только его отпечаток. Управлять ПК сможешь только ты; команда увидит его имя и доступность.
        </p>
        <button type="button" className="min-h-11 self-start text-meta font-semibold text-p-link" onClick={onInstall}>
          На ПК ещё нет приложения «Агенты»? Установить одной командой
        </button>
      </div>
    </Sheet>
  );
}

/** Task 03d3e54d: «Агенты» on a Windows PC with one command — no archive, no repository. The
 * command carries a key of this person for 30 minutes: it is not to be passed on. */
function InstallSheet({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [copied, setCopied] = useState("");
  const make = useMutation({
    mutationFn: () => api<{ command: string; expires_at: string }>("/api/v1/devices/installer", { method: "POST" }),
    onError: (e: Error) => toast.error(e.message),
  });
  const out = make.data;
  const copy = async () => {
    if (!out) return;
    try {
      await navigator.clipboard.writeText(out.command);
      setCopied("Команда скопирована.");
    } catch {
      setCopied("Выдели команду и скопируй вручную.");
    }
  };
  return (
    <Sheet open onClose={onClose} title="Установить «Агенты» на ПК"
      footer={out
        ? <Button block variant="strong" onClick={() => void copy()}>Скопировать команду</Button>
        : <Button block variant="strong" loading={make.isPending} onClick={() => make.mutate()}>Получить команду</Button>}>
      <div className="flex flex-col gap-3">
        <ol className="list-decimal space-y-1 pl-5 text-body text-p-text">
          <li>На ПК с Windows нажми Win+R (или открой PowerShell), вставь команду и нажми Enter.</li>
          <li>Она поставит Node.js, если его нет, и приложение «Агенты»: ярлык на рабочем столе, запуск при входе в Windows. Права администратора не нужны, займёт несколько минут.</li>
          <li>В открывшемся окне «Агенты» нажми «Подключить этот ПК» и введи код здесь: «Подключить ПК».</li>
        </ol>
        {out && (
          <>
            <pre className="select-all overflow-x-auto whitespace-pre-wrap break-all rounded-card border border-p-border bg-p-surface-2 px-4 py-3 text-meta text-p-text" data-install-command>
              {out.command}
            </pre>
            {copied && <p className="text-meta text-p-text-muted" role="status">{copied}</p>}
            <p className="text-meta text-p-text-muted">
              Команда твоя и действует 30 минут — не пересылай её. Повторный запуск обновит приложение; агентов и их папки он не трогает.
            </p>
          </>
        )}
        <p className="text-meta text-p-text-muted">
          Для запуска агентов в Codex нужны Codex CLI (есть в приложении Codex) и Python 3.11+: установщик проверит их и предложит поставить Python.
        </p>
      </div>
    </Sheet>
  );
}

function RevokeSheet({ device, onClose }: { device: DeviceOut; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const revoke = useMutation({
    mutationFn: () => Devices.revoke(device.id, true),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["devices"] });
      void qc.invalidateQueries({ queryKey: ["agents"] });
      toast.success(`ПК «${device.name}» отключён`);
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Sheet open onClose={onClose} title={`Отключить «${device.name}»?`}
      footer={<Button block variant="destructive" loading={revoke.isPending} onClick={() => revoke.mutate()}>Отключить и остановить его агентов</Button>}>
      <ul className="list-disc space-y-1 pl-5 text-body text-p-text">
        <li>Ключ ПК больше ничего не откроет: новых команд он не получит.</li>
        <li>Запуски, начатые на нём, остановятся; ПК закроет процессы агентов, которые запустил по командам. Другие его программы не тронет.</li>
        <li>Если ПК сейчас не в сети, остановку он подтвердит, когда выйдет на связь, — до этого здесь будет «подтверждения нет».</li>
      </ul>
    </Sheet>
  );
}

/** A command's facts in order, and what may still be done with it. */
export function CommandFacts({ command, onChanged }: { command: DeviceCommandOut; onChanged: (c: DeviceCommandOut) => void }) {
  const toast = useToast();
  const [sure, setSure] = useState(false);
  const act = useMutation({
    mutationFn: (what: "cancel" | "stop" | "resolve") => Devices[what](command.id),
    onSuccess: (c) => onChanged(c),
    onError: (e: Error) => toast.error(e instanceof ApiError ? e.message : String(e)),
  });
  const can = commandActions(command);
  return (
    <div className="flex flex-col gap-2" data-command={command.id}>
      {command.kind === "stop" && <p className="text-meta font-semibold text-p-text-muted">Остановка</p>}
      <ol className="space-y-1">
        {command.facts.map((f, i) => (
          <li key={`${f.key}-${i}`} className={f.bad ? "text-body text-p-blocked" : "text-body text-p-text"}>
            {f.text}{f.at && !f.bad ? <span className="text-meta text-p-text-muted"> · {ago(f.at)}</span> : null}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-2">
        {can.cancel && <Button size="compact" variant="secondary" loading={act.isPending} onClick={() => act.mutate("cancel")}>Отменить</Button>}
        {can.stop && <Button size="compact" variant="destructive" loading={act.isPending} onClick={() => act.mutate("stop")}>Остановить процесс</Button>}
        {can.resolve && !sure && <Button size="compact" variant="ghost" onClick={() => setSure(true)}>Процесса нет — снять резерв</Button>}
      </div>
      {can.resolve && sure && (
        <div className="space-y-2 rounded-card border border-p-border bg-p-surface-2 px-4 py-3">
          <p className="text-body text-p-text">ПК пропал во время запуска, и что с процессом — неизвестно. Если он всё-таки жив, после нового запуска агентов станет два.</p>
          <div className="flex gap-2">
            <Button size="compact" variant="destructive" loading={act.isPending} onClick={() => act.mutate("resolve")}>Снять резерв</Button>
            <Button size="compact" variant="secondary" onClick={() => setSure(false)}>Не снимать</Button>
          </div>
        </div>
      )}
    </div>
  );
}
